<template>
  <Modal
    v-model:visible="isVisible"
    class="brokerage-modal"
    :dismissable-mask="false"
    :header="
      brokerageInfo.data?.id
        ? t('common.editParam', { param: t('brokerages.brokerages') })
        : t('brokerages.sendToBrokerage')
    "
    size="md"
    @keypress.enter="onConfirm"
  >
    <div class="flex flex-gap-1 flex-column">
      <div class="brokerage-modal-carrier-profile">
        <div class="flex flex-column align-items-center justify-content-center">
          <CarrierAvatar
            v-if="internalValue.carrier"
            :carrier="internalValue.carrier"
            size="large"
          />
          <FontAwesomeIcon
            v-else
            class="carrier-icon text-5xl"
            :icon="['fas', 'truck']"
          />
          <h1 v-if="internalValue.carrier" class="carrier-name">
            {{ internalValue.carrier.name }}
            {{
              internalValue.carrier.scac?.length
                ? `(${internalValue.carrier.scac})`
                : ''
            }}
          </h1>
          <span v-else>
            {{
              t('common.noParamSelected', { param: t('brokerages.brokers') })
            }}
          </span>
        </div>
        <div v-if="internalValue.carrier" class="carrier-info-row">
          <KeyValuePair
            :label="t('common.name')"
            :value="internalValue.carrier.name"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.scac')"
            :value="internalValue.carrier.scac"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.mcNumber')"
            :value="internalValue.carrier.mcNumber"
            class="w-3"
          />
          <KeyValuePair
            :label="t('carriers.dotNumber')"
            :value="internalValue.carrier.dotNumber"
            class="w-3"
          />
        </div>
      </div>

      <form
        :aria-label="t('rateShop.quotes')"
        class="brokerage-modal-form flex flex-gap-1 flex-column"
      >
        <div class="brokerage-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-broker"
            :label="t('brokerages.brokers')"
            :errors="v$.carrier.$errors"
            class="w-6"
          >
            <CarrierQuickSearch
              :id="inputId"
              ref="firstInput"
              :model-value="internalValue.carrier"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="!!internalValue.id"
              :type="CapacityProviderType.BROKER"
              label-key="brokerages.brokers"
              append-to="body"
              @update:model-value="onBrokerSelected"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-carrier"
            :label="t('carriers.carriers')"
            class="w-6"
            optional
          >
            <CarrierQuickSearch
              :id="inputId"
              :model-value="brokeredCarrier ?? undefined"
              v-bind="ariaProps"
              append-to="body"
              @update:model-value="
                (val?: Carrier) =>
                  (internalValue.brokeredCarrier = val?.toCarrierRef())
              "
              @change="loadCarrierSettings($event.value)"
              @clear="carrierSettings.data = new CarrierSetting()"
            >
              <template #footer>
                <div class="brokerage-modal-carrier-dropdown-footer">
                  <AddButton
                    :label="t('carriers.carriers')"
                    @click="showAddAdHocBrokeredCarrierModal"
                  />
                </div>
              </template>
            </CarrierQuickSearch>
          </InputWrapper>
        </div>

        <div class="brokerage-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-requested-pickup-early"
            :label="earlyPickupMessage"
            :errors="v$.pickupEarliest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.pickupEarliest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.pickupTimeZone"
              :step-minute="15"
              :default-time="{ hour: 9, minute: 0 }"
              date-format="mm/dd/yy"
              hour-format="12"
              show-icon
              show-time
              :placeholder="earlyPickupMessage"
            />
          </InputWrapper>
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-requested-pickup-late"
            :label="latePickupMessage"
            :errors="v$.pickupLatest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.pickupLatest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.pickupTimeZone"
              :step-minute="15"
              date-format="mm/dd/yy"
              default-time="5pm"
              hour-format="12"
              show-icon
              show-time
              :placeholder="latePickupMessage"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-requested-delivery-early"
            :label="earlyDeliveryMessage"
            :errors="v$.deliveryEarliest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.deliveryEarliest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.deliveryTimeZone"
              :step-minute="15"
              :default-time="{ hour: 9, minute: 0 }"
              date-format="mm/dd/yy"
              hour-format="12"
              show-icon
              show-time
              :placeholder="earlyDeliveryMessage"
            />
          </InputWrapper>
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-requested-delivery-late"
            :label="lateDeliveryMessage"
            :errors="v$.deliveryLatest.$errors"
            class="w-3"
          >
            <RygenCalendar
              :id="inputId"
              v-model="internalBrokerageDates.deliveryLatest"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :time-zone="internalBrokerageDates.deliveryTimeZone"
              :step-minute="15"
              date-format="mm/dd/yy"
              default-time="5pm"
              hour-format="12"
              show-icon
              show-time
              :placeholder="lateDeliveryMessage"
            />
          </InputWrapper>
        </div>

        <div class="brokerage-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-mode"
            :label="t('shipments.modes')"
            class="w-4"
            :errors="v$.mode.$errors"
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.mode"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :options="availableModeOptions"
              :placeholder="t('shipments.modes')"
              :loading="carrierSettings.loading"
              append-to="body"
              option-label="label"
              option-value="value"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-service-level"
            :label="t('carriers.serviceLevels')"
            class="w-4"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.serviceType"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :options="availableServiceLevels"
              :loading="carrierSettings.loading"
              :placeholder="t('carriers.serviceLevels')"
              append-to="body"
            />
          </InputWrapper>
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-equipment-type"
            :label="
              t('common.paramTypes', { param: t('referenceData.equipment') })
            "
            class="w-4"
            optional
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalValue.equipmentType"
              v-bind="ariaProps"
              :options="availableEquipmentTypes"
              :loading="carrierSettings.loading"
              :placeholder="
                t('common.paramTypes', { param: t('referenceData.equipment') })
              "
              option-label="name"
              data-key="id"
              append-to="body"
              filter
              show-clear
            />
          </InputWrapper>
        </div>

        <div class="brokerage-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-linehaul"
            :errors="v$.rawLineHaulGrossCharge.$errors"
            :label="
              !!internalValue.id
                ? t('shipments.financials.linehaul')
                : t('brokerages.targetLinehaul')
            "
            class="w-4"
            optional
          >
            <InputNumber
              v-model="internalValue.rawLineHaulGrossCharge"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :input-id="inputId"
              mode="currency"
              class="w-full"
              :currency="internalValue.currencyCode ?? Currency.USD"
              :currency-display="currencyDisplayMode"
              use-grouping
              :placeholder="
                !!internalValue.id
                  ? t('shipments.financials.linehaul')
                  : t('brokerages.targetLinehaul')
              "
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputClasses, inputId, invalid }"
            input-id="brokerage-modal-fuel"
            :errors="v$.fuelSurchargeAmount.$errors"
            :label="
              !!internalValue.id
                ? t('shipments.financials.fuel')
                : t('brokerages.targetFuel')
            "
            class="w-4"
            optional
          >
            <InputNumber
              v-model="internalValue.fuelSurchargeAmount"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :input-id="inputId"
              mode="currency"
              class="w-full"
              :currency="internalValue.currencyCode ?? Currency.USD"
              :currency-display="currencyDisplayMode"
              use-grouping
              :placeholder="
                !!internalValue.id
                  ? t('shipments.financials.fuel')
                  : t('brokerages.targetFuel')
              "
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-currency"
            :label="t('currency.currency')"
            class="w-4"
          >
            <CurrencyDropdown
              :id="inputId"
              v-model="internalValue.currencyCode"
              v-bind="ariaProps"
              append-to="body"
            />
          </InputWrapper>
        </div>

        <div class="brokerage-modal-form-row">
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-accessorials"
            :label="t('referenceData.accessorials', 2)"
            class="w-full"
            optional
          >
            <RateQuoteAccessorials
              :id="inputId"
              v-model="internalValue.accessorials"
              v-bind="ariaProps"
              :available-accessorials="accessorialCodes.data"
              class="field"
            />
          </InputWrapper>
        </div>

        <div
          v-if="
            selectedBrokerSettings &&
            isEmailTender &&
            internalTenderConfig.configOptions
          "
          class="brokerage-modal-form-row"
        >
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-emails"
            :label="t('shipments.emailsCommaSeparated')"
            class="w-6"
          >
            <InputText
              :id="inputId"
              v-model="internalTenderConfig.configOptions.emails"
              v-bind="ariaProps"
              :placeholder="t('shipments.emailsCommaSeparated')"
            />
          </InputWrapper>

          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            input-id="brokerage-modal-notes"
            :label="t('orders.notes')"
            class="w-6"
          >
            <Textarea
              :id="inputId"
              v-model="internalTenderConfig.configOptions.notes"
              v-bind="ariaProps"
              :placeholder="t('orders.notes')"
            />
          </InputWrapper>
        </div>
      </form>
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        mode="primary"
        :label="t('common.next')"
        @click="onConfirm"
      />
    </template>
  </Modal>

  <AddAdHocBrokeredCarrierModal
    v-model:visible="isAddAdHocBrokeredCarrierModalVisible"
    @ad-hoc-brokered-carrier-added="onAdHocBrokeredCarrierAdded"
  />
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { ValidationRule } from '@vuelidate/core';
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import {
  computed,
  nextTick,
  provide,
  ref,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  afterDateTime,
  beforeDateTime,
  minValue,
  required,
  serverValidationPlaceholder,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  CapacityProviderType,
  CarrierRef,
  CarrierSetting,
  Currency,
  DropdownOption,
  ErrorResponse,
  EquipmentType,
  Loadable,
  Mode,
  TenderConfig,
  TenderMethod,
  Optional,
} from '@rygen/rygen-ui/model';
import { dedupeArray, notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  BrokerageDates,
  BrokeragePayload,
  Carrier,
  RateQuote,
  RateQuoteAccessorial,
  WellKnownService,
} from '@/model';
import { useAccessorialCodeStore } from '@/stores/AccessorialCode';
import { useEquipmentTypeStore } from '@/stores/EquipmentType';
import { useParentEntityStore } from '@/stores/ParentEntity';

import AddAdHocBrokeredCarrierModal from '@/components/brokerage/AddAdHocBrokeredCarrierModal.vue';
import CarrierQuickSearch from '@/components/carrier/CarrierQuickSearch.vue';
import { type BrokerageInfo } from '@/components/rateShop/editQuote/EditBrokerageQuoteRedesignModal.vue';
import RateQuoteAccessorials from '@/components/rateShop/RateQuoteAccessorials.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import CurrencyDropdown from '@rygen/rygen-ui/components/form/CurrencyDropdown.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  brokerageInfo: {
    type: Object as PropType<BrokerageInfo>,
    default: () => ({ loading: false }),
  },
  brokerageDates: {
    type: BrokerageDates,
    default: () => undefined,
  },
  isBrokeredFromShipment: {
    type: Boolean,
    default: false,
  },
});
const { brokerageInfo, brokerageDates, isBrokeredFromShipment } = toRefs(props);
const emit = defineEmits<{
  (event: 'confirm', payload: BrokeragePayload): void;
}>();

const firstInput = useTemplateRef('firstInput');

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const { equipmentTypes, loadEquipmentTypes } = useEquipmentTypeStore();
const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const toast = useToast();
const { uid } = useUid('brokerage-modal');
const isOptionalBrokerageDatesFeatFlagEnabled = useFlag(
  'optional-brokerage-dates-feat-flag',
);
provide('scope', uid);

const internalValue: Ref<RateQuote> = ref(
  new RateQuote(brokerageInfo.value.data),
);
const internalBrokerageDates: Ref<BrokerageDates> = ref(
  new BrokerageDates(brokerageDates?.value),
);
const selectedBrokerSettings: Ref<CarrierSetting | undefined> = ref();

const brokeredCarrier = computed(() => {
  return internalValue.value.brokeredCarrier
    ? Carrier.fromCarrierRef(internalValue.value.brokeredCarrier)
    : null;
});

const defaultTenderConfig: Ref<TenderConfig> = computed(() => {
  return (
    selectedBrokerSettings.value?.tenderConfig?.find((t) => t.defaultMethod) ??
    new TenderConfig()
  );
});

// Use computed prop for internalValue.brokerageTenderConfigOverride to
// guarantee it's always defined
const internalTenderConfig: Ref<TenderConfig> = computed({
  get() {
    const override = internalValue.value.brokerageTenderConfigOverride;
    return override?.method !== undefined
      ? override
      : defaultTenderConfig.value;
  },
  set: (newVal: TenderConfig) => {
    internalValue.value.brokerageTenderConfigOverride = new TenderConfig(
      newVal,
    );
  },
});

const earlyPickupMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledEarlyPickup')
    : t('orders.requestEarlyPickup');
});

const latePickupMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledLatePickup')
    : t('orders.requestLatePickup');
});

const earlyDeliveryMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledEarlyDelivery')
    : t('orders.requestEarlyDelivery');
});

const lateDeliveryMessage = computed(() => {
  return isBrokeredFromShipment.value
    ? t('shipments.dates.scheduledLateDelivery')
    : t('orders.requestLateDelivery');
});

type MaybeNestedValidationRule =
  | Record<string, ValidationRule>
  | Record<string, Record<string, ValidationRule>>;
const rules = computed(() => {
  const baseRules: Record<string, MaybeNestedValidationRule> = {
    carrier: {
      required: required(t('brokerages.brokers')),
    },
    rawLineHaulGrossCharge: {
      minValue: minValue(0.0, t('brokerages.targetLinehaul')),
    },
    fuelSurchargeAmount: {
      minValue: minValue(0.0, t('brokerages.targetFuel')),
    },
    mode: {
      required: required(t('shipments.modes')),
    },
    pickupEarliest: {
      required: required(earlyPickupMessage.value),
      before: beforeDateTime(
        () => combinedModel.value.pickupLatest,
        earlyPickupMessage.value,
        latePickupMessage.value,
        { strict: false },
      ),
    },
    pickupLatest: { serverValidationPlaceholder },
    deliveryEarliest: {
      before: beforeDateTime(
        () => combinedModel.value.deliveryLatest,
        earlyDeliveryMessage.value,
        lateDeliveryMessage.value,
        { strict: false },
      ),
      afterEarliestPickup: afterDateTime(
        () => combinedModel.value.pickupEarliest,
      ),
    },
    deliveryLatest: {
      afterEarliestPickup: afterDateTime(
        () => combinedModel.value.pickupEarliest,
      ),
    },
  };

  if (!isOptionalBrokerageDatesFeatFlagEnabled.value) {
    baseRules.pickupLatest = {
      ...baseRules.pickupLatest,
      required: required(latePickupMessage.value),
    };

    baseRules.deliveryEarliest = {
      ...baseRules.deliveryEarliest,
      required: required(earlyDeliveryMessage.value),
    };

    baseRules.deliveryLatest = {
      ...baseRules.deliveryLatest,
      required: required(lateDeliveryMessage.value),
    };
  }

  return baseRules;
});

const combinedModel = computed(() => {
  return {
    ...internalValue.value,
    ...internalBrokerageDates.value,
  };
});

const { setServerErrors, v$, validate } = useValidations(
  combinedModel,
  rules,
  uid,
);

const carrierSettings: Ref<Loadable<CarrierSetting>> = ref({
  loading: false,
  data: new CarrierSetting(),
});

const isEmailTender = computed(() => {
  return selectedBrokerSettings.value?.tenderConfig?.find(
    (t) => t.method === TenderMethod.EMAIL,
  )?.defaultMethod;
});

const isAddAdHocBrokeredCarrierModalVisible = ref(false);

const selectedBroker = computed((): Carrier => {
  return selectedBrokerSettings.value?.carrier;
});

const availableModes: Ref<Mode[]> = computed(() => {
  const brokerModes = selectedBroker.value?.modes || [];
  const carrierModes = carrierSettings.value?.data?.modes || [];
  return brokerModes.concat(carrierModes)?.length > 0
    ? dedupeArray(brokerModes.concat(carrierModes))
    : Object.values(Mode);
});

const availableModeOptions: Ref<DropdownOption<Mode>[]> = computed(() => {
  return availableModes.value.map((mode) => ({
    label: t(`enumMap.Mode.${mode}`),
    value: mode,
  }));
});

const availableServiceLevels: Ref<string[]> = computed(() => {
  const brokerServiceLevels =
    selectedBroker.value?.serviceLevels?.map((x) => x.name)?.filter(notNull) ||
    [];
  const carrierServiceLevels =
    carrierSettings.value?.data?.serviceLevels
      ?.map((x) => x.name)
      ?.filter(notNull) || [];
  return brokerServiceLevels.concat(carrierServiceLevels).length > 0
    ? dedupeArray(brokerServiceLevels.concat(carrierServiceLevels))
    : [WellKnownService.STANDARD];
});

const availableEquipmentTypes: Ref<EquipmentType[]> = computed(() => {
  const brokerEquipmentTypes = selectedBroker.value?.equipmentTypes || [];
  const carrierEquipmentTypes =
    carrierSettings.value?.data?.equipmentTypes || [];
  return brokerEquipmentTypes.concat(carrierEquipmentTypes).length > 0
    ? dedupeArray(brokerEquipmentTypes.concat(carrierEquipmentTypes))
    : equipmentTypes.value.data || [];
});

const currencyDisplayMode = computed(() => {
  return internalValue.value.currencyCode === Currency.USD ? 'symbol' : 'code';
});

const setDefaultMode = (availableModes: Mode[]) => {
  if (!internalValue.value.mode && availableModes.length === 1) {
    internalValue.value.mode = availableModes[0];
  }
};

const setDefaultServiceLevel = (availableServiceLevels: string[]) => {
  if (!internalValue.value.serviceType && availableServiceLevels.length === 1) {
    internalValue.value.serviceType = availableServiceLevels[0];
  }
};

const verifyMode = () => {
  if (internalValue.value.mode) {
    if (!availableModes.value.find((m) => m === internalValue.value.mode)) {
      internalValue.value.mode = availableModes.value[0];
    }
  }
};

const verifyServiceType = () => {
  if (internalValue.value.serviceType) {
    if (
      !availableServiceLevels.value.find(
        (s) => s === internalValue.value.serviceType,
      )
    ) {
      internalValue.value.serviceType = availableServiceLevels.value[0];
    }
  }
};

const verifyEquipmentType = () => {
  if (internalValue.value.equipmentType) {
    if (
      !availableEquipmentTypes.value.find(
        (e) => e.id === internalValue.value.equipmentType?.id,
      )
    ) {
      internalValue.value.equipmentType = availableEquipmentTypes.value[0];
    }
  }
};

const verifyDropdowns = () => {
  verifyMode();
  verifyServiceType();
  verifyEquipmentType();
};

const loadCarrierSettings = async (carrier: Carrier) => {
  if (!carrier.scac || !authorization.hasAuthority('client', 'read')) {
    return;
  }

  try {
    carrierSettings.value.loading = true;
    carrierSettings.value.data =
      carrier.type === CapacityProviderType.CARRIER
        ? await api.carrier.findCarrierSettingsForScac(carrier.scac)
        : await api.broker.findBrokerSettingsForScac(carrier.scac);
    verifyDropdowns();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // No CarrierSettings found but no need to show an error
      carrierSettings.value.data = new CarrierSetting();
      return;
    }
    console.error("Error loading selected carrier's settings", e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.carrierSettings', 2),
      }),
      error: e,
    });
  } finally {
    carrierSettings.value.loading = false;
  }
};

// Two `immediate` watches and the dropdown all trigger this, so a slow response
// for an earlier broker must not land on top of a newer selection.
let requestedBrokerScac: string | undefined;

const loadSelectedBrokerSettings = async (scac?: string) => {
  if (selectedBrokerSettings.value?.carrier?.scac === scac) {
    return;
  }

  requestedBrokerScac = scac;

  if (!scac || !authorization.hasAuthority('client', 'read')) {
    selectedBrokerSettings.value = undefined;
    return;
  }

  try {
    const setting = await api.broker.findBrokerSettingsForScac(scac);

    if (requestedBrokerScac === scac) {
      selectedBrokerSettings.value = setting;
    }
  } catch (e) {
    if (axios.isCancel(e) || requestedBrokerScac !== scac) {
      return;
    }

    selectedBrokerSettings.value = undefined;

    if (axios.isAxiosError(e) && e.response?.status === 404) {
      // A quote whose broker is no longer configured for the client is a normal
      // miss, so the dropdown just stays empty.
      return;
    }

    console.error("Error loading selected broker's settings", e);
  }
};

const onBrokerSelected = (broker?: Carrier) => {
  internalValue.value.carrier = broker;
  loadSelectedBrokerSettings(broker?.scac);
};

const onAdHocBrokeredCarrierAdded = (carrierRef: CarrierRef): void => {
  internalValue.value.brokeredCarrier = new CarrierRef(carrierRef);
};

const showAddAdHocBrokeredCarrierModal = (): void => {
  isAddAdHocBrokeredCarrierModalVisible.value = true;
};

const populateAccessorials = () => {
  const topLevelAccessorials =
    parentEntity.value?.requestedAccessorials?.map(
      (code) => new RateQuoteAccessorial({ code }),
    ) ?? [];
  const stopLevelAccessorials = (parentEntity.value?.stops ?? [])
    .map((s) => {
      const stopSequenceNumber = s.sequenceNumber;
      return s.requestedAccessorials.map(
        (code) => new RateQuoteAccessorial({ code, stopSequenceNumber }),
      );
    })
    .flat();

  internalValue.value.accessorials = [
    ...topLevelAccessorials,
    ...stopLevelAccessorials,
  ];
};

/**
 * This modal cannot modify an uplift rule — it has no uplift inputs at all — so any rule on the quote came from
 * rating or the redesigned modal. It can still edit every charge those rules were derived from (linehaul, fuel,
 * accessorial charge), and a percent rule's amount is only meaningful against the charge it was calculated from.
 * Rather than guess which edits invalidate which rule, never submit one: the backend rebuilds a flat rule from
 * each uplift amount.
 *
 * With the AR/AP flag off this changes nothing, as the backend replaces these rules regardless. It matters only
 * when the frontend renders this modal while the backend evaluates the flag as on, which would otherwise save a
 * brokerage quote with an uplift amount its rule contradicts.
 */
const resetUpliftRules = () => {
  internalValue.value.upliftRule = undefined;
  internalValue.value.fuelUpliftRule = undefined;
  internalValue.value.accessorials.forEach((accessorial) => {
    accessorial.upliftRule = undefined;
  });
};

const onConfirm = async () => {
  if (await validate()) {
    // Set undefined fuel/linehaul to 0 to pass database validation
    if (!internalValue.value.rawLineHaulGrossCharge) {
      internalValue.value.rawLineHaulGrossCharge = 0;
    }
    if (!internalValue.value.fuelSurchargeAmount) {
      internalValue.value.fuelSurchargeAmount = 0;
    }
    internalValue.value.brokerageTenderConfigOverride =
      internalTenderConfig.value;

    resetUpliftRules();
    isVisible.value = false;
    emit('confirm', {
      brokerageQuote: internalValue.value,
      brokerageDates: internalBrokerageDates.value,
    });
  }
};

watch(
  () => brokerageInfo.value.data,
  (newVal: RateQuote | undefined) => {
    internalValue.value = new RateQuote(newVal);
    v$.value.$reset();

    if (internalValue.value.carrier) {
      loadSelectedBrokerSettings(internalValue.value.carrier.scac);
    }

    nextTick(() => {
      setDefaultMode(availableModes.value);
      setDefaultServiceLevel(availableServiceLevels.value);
    });
  },
  { deep: true, immediate: true },
);

watch(
  () => brokerageInfo.value.error,
  (newVal: Optional<ErrorResponse>) => {
    setServerErrors(newVal);
  },
);

watch(
  selectedBrokerSettings,
  (newVal?: CarrierSetting, oldVal?: CarrierSetting) => {
    if (newVal) {
      internalValue.value.carrier = newVal.carrier;

      const isSwitchingClientCarrier =
        !!oldVal && newVal.carrier?.scac !== oldVal.carrier?.scac;
      const isEmptyConfigOptions =
        internalTenderConfig.value.configOptions?.isEmpty() ?? true;
      if (isSwitchingClientCarrier || isEmptyConfigOptions) {
        const newDefaultTenderConfig =
          newVal.tenderConfig?.find((t) => t.defaultMethod) ??
          new TenderConfig();
        internalTenderConfig.value = new TenderConfig(newDefaultTenderConfig);
      }
    }
  },
);

watch(
  () => brokerageDates?.value,
  (newVal?: BrokerageDates) => {
    internalBrokerageDates.value = new BrokerageDates(newVal);
    nextTick(() => v$.value.$reset());
  },
  { deep: true, immediate: true },
);

watch(
  isVisible,
  (newVal) => {
    if (newVal && !brokerageInfo?.value?.error?.errorDetails?.fieldErrors) {
      loadRequestableAccessorialCodes();
      loadEquipmentTypes();

      nextTick(() => {
        const el = firstInput.value?.$el as HTMLElement | null;
        const input = el?.querySelector(
          'input,select,textarea',
        ) as HTMLElement | null;
        input?.focus();
      });

      internalValue.value = new RateQuote(brokerageInfo.value.data);
      internalBrokerageDates.value = new BrokerageDates(brokerageDates?.value);
      loadSelectedBrokerSettings(internalValue.value.carrier?.scac);

      if (!brokerageInfo.value.data?.id) {
        populateAccessorials();
      }

      if (internalValue.value.carrier) {
        loadCarrierSettings(internalValue.value.carrier);
      }

      nextTick(() => v$.value.$reset());
    }
  },
  { immediate: true },
);

watch(availableModes, (newVal: Mode[]) => {
  setDefaultMode(newVal);
});

watch(availableServiceLevels, (newVal: string[]) => {
  setDefaultServiceLevel(newVal);
});
</script>

<style>
.brokerage-modal {
  .brokerage-modal-form {
    overflow: auto;
    scroll-padding: var(--sp-3);

    .brokerage-modal-form-row {
      display: flex;
      gap: var(--sp-3);
    }
  }

  .brokerage-modal-carrier-profile {
    margin-bottom: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);

    .carrier-icon {
      margin-bottom: var(--sp-3);
    }

    .carrier-name {
      margin-bottom: var(--sp-6);
    }

    .carrier-info-row {
      display: flex;
      gap: var(--sp-3);
      justify-content: center;
    }
  }

  .brokerage-modal-carrier-dropdown {
    .brokerage-modal-carrier-dropdown-footer {
      border-top: var(--divider);

      & > button.add-button.p-button {
        width: 100%;
        justify-content: left;
        padding: var(--sp-3);
      }
    }
  }
}
</style>
